/**
 * Address management type definitions for B2B customer address management
 */

export enum AddressType {
  BILLING = 'billing',
  SHIPPING = 'shipping',
  BOTH = 'both'
}

export enum AddressChangeType {
  CREATED = 'created',
  UPDATED = 'updated',
  DELETED = 'deleted',
  SET_DEFAULT = 'set_default',
  ACTIVATED = 'activated',
  DEACTIVATED = 'deactivated'
}

// UI Helper constants
export const ADDRESS_TYPE_OPTIONS = [
  { value: AddressType.BILLING, label: 'Billing Address' },
  { value: AddressType.SHIPPING, label: 'Shipping Address' },
  { value: AddressType.BOTH, label: 'Billing & Shipping' }
] as const;

export const ADDRESS_TYPE_LABELS: Record<AddressType, string> = {
  [AddressType.BILLING]: 'Billing Address',
  [AddressType.SHIPPING]: 'Shipping Address',
  [AddressType.BOTH]: 'Billing & Shipping'
};

// Indian states for dropdown
export const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep'
] as const;

export type IndianState = typeof INDIAN_STATES[number];

// Validation constraints
export const ADDRESS_VALIDATION = {
  POSTAL_CODE: {
    MIN_LENGTH: 5,
    MAX_LENGTH: 10,
    PATTERN: /^[0-9]{5,10}$/
  },
  PHONE: {
    PATTERN: /^[+]?[0-9]{10,15}$/
  },
  GSTIN: {
    PATTERN: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
  }
} as const;

export interface CustomerAddress {
  id: string;
  customer_id: string;
  address_type: AddressType;
  is_default: boolean;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  contact_person?: string;
  contact_phone?: string;
  gstin?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerAddressCreateData {
  customer_id: string;
  address_type: AddressType;
  is_default?: boolean;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  contact_person?: string;
  contact_phone?: string;
  gstin?: string;
  is_active?: boolean;
}

export interface CustomerAddressUpdateData {
  address_type?: AddressType;
  is_default?: boolean;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  contact_person?: string;
  contact_phone?: string;
  gstin?: string;
  is_active?: boolean;
}

export interface CustomerAddressListResponse {
  addresses: CustomerAddress[];
  total_count: number;
  billing_addresses: number;
  shipping_addresses: number;
  both_addresses: number;
}

export interface AddressSearchFilters {
  customer_id?: string;
  address_type?: AddressType;
  city?: string;
  state?: string;
  is_default?: boolean;
  is_active?: boolean;
  search_term?: string;
}

export interface AddressSearchParams extends AddressSearchFilters {
  page?: number;
  per_page?: number;
  sort_by?: 'created_at' | 'updated_at' | 'city' | 'state';
  sort_order?: 'asc' | 'desc';
}

export interface AddressValidationErrors {
  address_line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  contact_phone?: string;
  gstin?: string;
}

export interface AddressHistory {
  id: string;
  address_id: string;
  customer_id: string;
  change_type: AddressChangeType;
  old_values?: Partial<CustomerAddress>;
  new_values?: Partial<CustomerAddress>;
  changed_by_admin_id: string;
  changed_by_admin?: {
    id: string;
    name: string;
    email: string;
  };
  changed_at: string;
  description: string;
  ip_address?: string; // For audit purposes
}

export interface AddressHistoryResponse {
  address_id?: string;
  customer_id: string;
  history: AddressHistory[];
  total_count: number;
}

// Utility types for common operations
export type AddressFormData = Omit<CustomerAddressCreateData, 'customer_id'>;
export type AddressDisplayData = Pick<CustomerAddress, 'id' | 'address_line1' | 'address_line2' | 'city' | 'state' | 'postal_code' | 'address_type' | 'is_default'>;

// For address selection components
export interface AddressOption {
  value: string;
  label: string;
  address: CustomerAddress;
}

// Validation helper functions
export const validateGSTIN = (gstin: string): boolean => {
  return ADDRESS_VALIDATION.GSTIN.PATTERN.test(gstin);
};

export const validatePostalCode = (postalCode: string): boolean => {
  return ADDRESS_VALIDATION.POSTAL_CODE.PATTERN.test(postalCode);
};

export const validatePhone = (phone: string): boolean => {
  return ADDRESS_VALIDATION.PHONE.PATTERN.test(phone);
};

export const formatAddress = (address: CustomerAddress): string => {
  const parts = [
    address.address_line1,
    address.address_line2,
    address.city,
    address.state,
    address.postal_code
  ].filter(Boolean);
  
  return parts.join(', ');
};